import React from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  Camera, Upload, Sparkles, Wand2, CheckCircle2, Loader2, X, Plus,
  Download, ImagePlus, BadgeCheck, ShieldAlert, Trash2, Star,
  RefreshCw, ChevronRight, ArrowLeft, ArrowRight, Settings, FlaskConical,
} from "lucide-react";
import api, { resolveImageUrl, API_BASE } from "../lib/api";
import { toast } from "sonner";

const ASPECT_RATIOS = [
  { key: "4:3", label: "4:3 (Listing)" },
  { key: "16:9", label: "16:9 (Wide)" },
  { key: "1:1", label: "1:1 (Social)" },
];

const STYLE_TYPES = [
  { key: "dark_showroom", label: "Dark Showroom" },
  { key: "bright_studio", label: "Bright Studio" },
  { key: "outdoor", label: "Outdoor" },
  { key: "social", label: "Social" },
  { key: "custom", label: "Custom" },
];

const GENERATION_MODES = [
  {
    key: "premium_ai_blend",
    label: "Premium KI-Blend",
    hint: "KI integriert das Auto natürlich in den Showroom (Licht, Schatten, Bodenkontakt). Empfohlen.",
  },
  {
    key: "deterministic_cutout",
    label: "Deterministisch",
    hint: "Reines Pillow-Compositing (rembg + Schatten). Identisches Ergebnis bei gleichem Input.",
  },
];

const MODE_BADGE_STYLE = {
  premium_ai_blend: "text-purple-200 bg-purple-500/15 border-purple-500/30",
  deterministic_cutout: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
};

const PhotoStudio = ({ vehicles, defaultVehicleId, onRefreshVehicles }) => {
  const [step, setStep] = React.useState(1);
  const [selectedVehicleId, setSelectedVehicleId] = React.useState(defaultVehicleId || "");
  const [sourceImage, setSourceImage] = React.useState(null);  // { url, path }
  const [sourceFile, setSourceFile] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);

  const [backgrounds, setBackgrounds] = React.useState([]);
  const [selectedBgId, setSelectedBgId] = React.useState("");
  const [aspectRatio, setAspectRatio] = React.useState("4:3");
  const [customNotes, setCustomNotes] = React.useState("");
  const [generationMode, setGenerationMode] = React.useState("premium_ai_blend");

  const [generating, setGenerating] = React.useState(false);
  const [generation, setGeneration] = React.useState(null);
  const [qualityChecking, setQualityChecking] = React.useState(false);
  const [qualityResult, setQualityResult] = React.useState(null);

  const [bgManagerOpen, setBgManagerOpen] = React.useState(false);

  const selectedBg = backgrounds.find((b) => b.id === selectedBgId);

  React.useEffect(() => {
    if (defaultVehicleId) setSelectedVehicleId(defaultVehicleId);
  }, [defaultVehicleId]);

  const fetchBackgrounds = React.useCallback(async () => {
    const res = await api.get("/admin/backgrounds");
    setBackgrounds(res.data);
    if (!selectedBgId) {
      const def = res.data.find((b) => b.is_default && b.active) || res.data.find((b) => b.active);
      if (def) {
        setSelectedBgId(def.id);
        setAspectRatio(def.aspect_ratio || "4:3");
      }
    }
  }, [selectedBgId]);

  React.useEffect(() => { fetchBackgrounds(); }, [fetchBackgrounds]);

  const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId);

  // ---------- Step handlers ----------
  const handleFile = async (file) => {
    if (!file) return;
    setSourceFile(file);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSourceImage({ url: res.data.url, path: res.data.path });
      toast.success("Foto hochgeladen");
      // Auto-advance to next step
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const generate = async () => {
    if (!sourceImage || !selectedBgId) {
      toast.error("Bitte Quellbild und Hintergrund auswählen.");
      return;
    }
    setGenerating(true);
    setGeneration(null);
    setQualityResult(null);
    try {
      const { data } = await api.post("/admin/photo-studio/generate", {
        vehicle_id: selectedVehicleId || null,
        vehicle_name: selectedVehicle ? `${selectedVehicle.make} ${selectedVehicle.model}` : "",
        source_image_url: sourceImage.url,
        background_id: selectedBgId,
        aspect_ratio: aspectRatio,
        custom_notes: customNotes,
        mode: generationMode,
      });
      setGeneration(data);
      setStep(5);
      if (data.is_demo) {
        toast.info("Demo-Bild generiert (kein KI-Modell verfügbar).");
      } else if (data.mode_requested !== data.mode_used) {
        toast.warning(`KI-Blend nicht verfügbar – auf "${data.mode_label || "Deterministisch"}" zurückgefallen.`);
      } else {
        toast.success(`Studio-Bild generiert · ${data.mode_label || ""}`);
      }
    } catch (err) {
      toast.error("Generierung fehlgeschlagen");
    } finally {
      setGenerating(false);
    }
  };

  const runQualityCheck = async () => {
    if (!generation) return;
    setQualityChecking(true);
    try {
      const { data } = await api.post("/admin/photo-studio/quality-check", {
        image_url: generation.output_image_url,
      });
      setQualityResult(data);
    } catch (err) {
      toast.error("Qualitätsprüfung fehlgeschlagen");
    } finally {
      setQualityChecking(false);
    }
  };

  const saveToVehicle = async () => {
    if (!generation || !selectedVehicleId) {
      toast.error("Wählen Sie ein Fahrzeug zum Speichern aus.");
      return;
    }
    try {
      await api.post("/admin/photo-studio/save-to-vehicle", {
        vehicle_id: selectedVehicleId,
        image_url: generation.output_image_url,
      });
      toast.success("Bild dem Fahrzeug hinzugefügt");
      onRefreshVehicles?.();
    } catch (err) {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const downloadImage = async () => {
    if (!generation) return;
    const url = resolveImageUrl(generation.output_image_url);
    const a = document.createElement("a");
    a.href = url;
    a.download = `na-studio-${generation.id.slice(0, 8)}.png`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const resetFlow = () => {
    setSourceImage(null);
    setSourceFile(null);
    setGeneration(null);
    setQualityResult(null);
    setCustomNotes("");
    setStep(1);
  };

  return (
    <div className="space-y-6" data-testid="photo-studio-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[var(--na-red)]/15 text-[var(--na-red)] flex items-center justify-center">
              <Camera className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-display text-xl lg:text-2xl font-semibold text-white tracking-tight">Photo Studio</h2>
              <p className="text-sm text-zinc-400">Rohfotos in markenkonforme Showroom-Bilder verwandeln.</p>
            </div>
          </div>
        </div>
        <Button
          onClick={() => setBgManagerOpen(true)}
          variant="outline"
          data-testid="photo-studio-manage-backgrounds"
          className="border-white/15 text-white bg-transparent hover:bg-white/5 rounded-xl"
        >
          <Settings className="w-4 h-4 mr-2" /> Hintergründe verwalten
        </Button>
      </div>

      {/* Stepper */}
      <Stepper step={step} />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left column: workflow */}
        <div className="lg:col-span-2 space-y-6">
          {/* Step 1: vehicle */}
          <Section title="01 — Fahrzeug wählen" active={step >= 1}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">Fahrzeug</Label>
                <Select value={selectedVehicleId || "_test"} onValueChange={(v) => setSelectedVehicleId(v === "_test" ? "" : v)}>
                  <SelectTrigger data-testid="studio-vehicle-select" className="bg-[#1A1A22] border-white/10 text-white rounded-xl">
                    <SelectValue placeholder="Fahrzeug auswählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2E2E3C] border-white/10 text-white max-h-80">
                    <SelectItem value="_test" className="focus:bg-white/10 focus:text-white">Ohne Fahrzeug testen</SelectItem>
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id} className="focus:bg-white/10 focus:text-white">
                        {v.make} {v.model} · {v.year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => setStep(2)} data-testid="studio-step1-next" className="bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl">
                Weiter <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </Section>

          {/* Step 2: upload */}
          <Section title="02 — Foto hochladen" active={step >= 2}>
            {sourceImage ? (
              <div className="flex items-center gap-4">
                <div className="w-32 h-24 rounded-xl overflow-hidden border border-white/10 flex-shrink-0">
                  <img src={resolveImageUrl(sourceImage.url)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{sourceFile?.name || "Hochgeladenes Foto"}</p>
                  <p className="text-xs text-zinc-500">Original wird separat gespeichert</p>
                </div>
                <Button variant="outline" onClick={() => { setSourceImage(null); setSourceFile(null); }} data-testid="studio-source-clear" className="border-white/15 text-white bg-transparent hover:bg-white/5 rounded-xl">
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Ändern
                </Button>
              </div>
            ) : (
              <label className="block">
                <input
                  type="file"
                  accept="image/*,.heic,.heif"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="studio-source-upload"
                />
                <div className="rounded-2xl border-2 border-dashed border-white/15 hover:border-[var(--na-red)]/60 bg-[#1A1A22] hover:bg-[#1E1E28] p-8 lg:p-12 text-center cursor-pointer transition-colors">
                  {uploading ? (
                    <>
                      <Loader2 className="w-8 h-8 text-[var(--na-red)] mx-auto mb-3 animate-spin" />
                      <p className="text-sm text-white">Wird hochgeladen…</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-3" />
                      <p className="text-sm text-white font-medium">Foto hochladen</p>
                      <p className="text-xs text-zinc-500 mt-1">JPG, PNG, WebP oder iPhone-HEIC · max 20 MB</p>
                    </>
                  )}
                </div>
              </label>
            )}
          </Section>

          {/* Step 3: choose background */}
          <Section title="03 — Hintergrund wählen" active={step >= 3}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {backgrounds.filter((b) => b.active).map((bg) => (
                <button
                  key={bg.id}
                  type="button"
                  onClick={() => { setSelectedBgId(bg.id); setAspectRatio(bg.aspect_ratio || "4:3"); }}
                  data-testid={`studio-bg-${bg.key || bg.id}`}
                  className={`group relative rounded-2xl overflow-hidden border-2 transition-all text-left ${selectedBgId === bg.id ? "border-[var(--na-red)]" : "border-white/5 hover:border-white/15"}`}
                >
                  <div className="aspect-[4/3] bg-[#1A1A22]">
                    {bg.preview_url ? (
                      <img src={bg.preview_url} alt={bg.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs">Kein Vorschaubild</div>
                    )}
                  </div>
                  <div className="p-3 bg-[#2E2E3C]">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-display font-semibold text-white truncate">{bg.name}</p>
                      {bg.is_default && (<Star className="w-3.5 h-3.5 text-yellow-300 flex-shrink-0" fill="currentColor" />)}
                    </div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{bg.style_type}</p>
                  </div>
                  {selectedBgId === bg.id && (
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[var(--na-red)] text-white flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </Section>

          {/* Step 4: adjust */}
          <Section title="04 — Anpassen" active={step >= 4 || (step === 3 && selectedBgId)}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">Ergebnis-Modus</Label>
                <Select value={generationMode} onValueChange={setGenerationMode}>
                  <SelectTrigger data-testid="studio-mode-select" className="bg-[#1A1A22] border-white/10 text-white rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2E2E3C] border-white/10 text-white">
                    {GENERATION_MODES.map((m) => (
                      <SelectItem
                        key={m.key}
                        value={m.key}
                        data-testid={`studio-mode-option-${m.key}`}
                        className="focus:bg-white/10 focus:text-white"
                      >
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">
                  {GENERATION_MODES.find((m) => m.key === generationMode)?.hint}
                </p>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">Format</Label>
                <Select value={aspectRatio} onValueChange={setAspectRatio}>
                  <SelectTrigger data-testid="studio-aspect-select" className="bg-[#1A1A22] border-white/10 text-white rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2E2E3C] border-white/10 text-white">
                    {ASPECT_RATIOS.map((a) => (<SelectItem key={a.key} value={a.key} className="focus:bg-white/10 focus:text-white">{a.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">Zusätzliche Hinweise (optional)</Label>
                <Input
                  data-testid="studio-custom-notes"
                  value={customNotes}
                  onChange={(e) => setCustomNotes(e.target.value)}
                  placeholder="z.B. Auto mehr nach links, leichte Reflexion am Boden…"
                  className="bg-[#1A1A22] border-white/10 text-white rounded-xl"
                />
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button
                onClick={generate}
                disabled={!sourceImage || !selectedBgId || generating}
                data-testid="studio-generate-button"
                className="bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl tracking-wide red-glow"
              >
                {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> KI generiert…</> : <><Wand2 className="w-4 h-4 mr-2" /> Vorschau generieren</>}
              </Button>
            </div>
          </Section>

          {/* Step 5: result */}
          {generation && (
            <Section title="05 — Ergebnis" active>
              <div className="rounded-2xl border border-white/10 bg-[#1A1A22] overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2 border-b border-white/5">
                  {/* Original (source) */}
                  <div className="relative border-b md:border-b-0 md:border-r border-white/5">
                    <div className={`relative w-full ${aspectRatio === "1:1" ? "aspect-square" : aspectRatio === "16:9" ? "aspect-video" : "aspect-[4/3]"} bg-black`}>
                      <img
                        src={resolveImageUrl(generation.source_image_url)}
                        alt="Original"
                        data-testid="studio-result-source-image"
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-300 bg-black/55 border border-white/10 rounded-full px-2 py-0.5">
                        Original
                      </span>
                    </div>
                  </div>
                  {/* Generated */}
                  <div className="relative">
                    <div className={`relative w-full ${aspectRatio === "1:1" ? "aspect-square" : aspectRatio === "16:9" ? "aspect-video" : "aspect-[4/3]"} bg-black`}>
                      <img
                        src={resolveImageUrl(generation.output_image_url)}
                        alt="Generated"
                        data-testid="studio-result-image"
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white bg-[var(--na-red)]/90 border border-[var(--na-red)] rounded-full px-2 py-0.5">
                        Ergebnis
                      </span>
                      {generation.is_demo && (
                        <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-yellow-300/90 bg-yellow-500/15 border border-yellow-500/30 rounded-full px-2 py-0.5">
                          <FlaskConical className="w-3 h-3" /> Demo
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-4 lg:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm text-zinc-400">Hintergrund: <span className="text-white">{generation.background_name}</span> · {generation.aspect_ratio}</p>
                      <span
                        data-testid="studio-result-mode-badge"
                        className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${MODE_BADGE_STYLE[generation.mode_used] || MODE_BADGE_STYLE.deterministic_cutout}`}
                      >
                        {generation.mode_used === "premium_ai_blend" ? <Sparkles className="w-2.5 h-2.5" /> : <CheckCircle2 className="w-2.5 h-2.5" />}
                        {generation.mode_label || (generation.mode_used === "premium_ai_blend" ? "Premium KI-Blend" : "Deterministisch")}
                      </span>
                      {generation.mode_requested && generation.mode_requested !== generation.mode_used && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
                          Fallback
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={runQualityCheck}
                        disabled={qualityChecking}
                        variant="outline"
                        data-testid="studio-quality-check"
                        className="border-white/15 text-white bg-transparent hover:bg-white/5 rounded-xl"
                      >
                        {qualityChecking ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Prüfung…</> : <><Sparkles className="w-4 h-4 mr-1.5" /> KI Qualitätscheck</>}
                      </Button>
                      <Button onClick={downloadImage} variant="outline" data-testid="studio-download" className="border-white/15 text-white bg-transparent hover:bg-white/5 rounded-xl">
                        <Download className="w-4 h-4 mr-1.5" /> Herunterladen
                      </Button>
                      <Button
                        onClick={saveToVehicle}
                        disabled={!selectedVehicleId}
                        data-testid="studio-save-to-vehicle"
                        className="bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl"
                      >
                        <ImagePlus className="w-4 h-4 mr-1.5" /> Als Fahrzeugbild speichern
                      </Button>
                    </div>
                  </div>

                  {qualityResult && (
                    <div className="mt-3 rounded-xl border border-white/10 bg-[#2E2E3C] p-3 text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <BadgeCheck className="w-4 h-4 text-[var(--na-red)]" />
                        <span className="font-medium text-white">{qualityResult.rating}</span>
                      </div>
                      <p className="text-xs text-zinc-300">{qualityResult.reasoning}</p>
                    </div>
                  )}

                  <div className="mt-4 pt-3 border-t border-white/5 flex flex-wrap items-center gap-2">
                    <Button variant="outline" onClick={generate} disabled={generating} data-testid="studio-regenerate" className="border-white/15 text-white bg-transparent hover:bg-white/5 rounded-xl">
                      <RefreshCw className="w-4 h-4 mr-1.5" /> Neu generieren
                    </Button>
                    <Button variant="outline" onClick={resetFlow} className="border-white/15 text-white bg-transparent hover:bg-white/5 rounded-xl">
                      <ArrowLeft className="w-4 h-4 mr-1.5" /> Neues Foto
                    </Button>
                  </div>
                </div>
              </div>
            </Section>
          )}
        </div>

        {/* Right column: summary */}
        <aside className="space-y-4 lg:sticky lg:top-24 self-start">
          <div className="rounded-2xl border border-white/5 bg-[#2E2E3C] p-5 space-y-3">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Aktuelle Auswahl</p>
            <SummaryRow label="Fahrzeug" value={selectedVehicle ? `${selectedVehicle.make} ${selectedVehicle.model}` : "Test-Modus"} />
            <SummaryRow label="Quellbild" value={sourceImage ? "Hochgeladen" : "—"} />
            <SummaryRow label="Hintergrund" value={backgrounds.find((b) => b.id === selectedBgId)?.name || "—"} />
            <SummaryRow label="Modus" value={GENERATION_MODES.find((m) => m.key === generationMode)?.label || "—"} />
            <SummaryRow label="Format" value={aspectRatio} />
          </div>
          {sourceImage && (
            <div className="rounded-2xl border border-white/5 bg-[#2E2E3C] p-3">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Quellbild Vorschau</p>
              <img src={resolveImageUrl(sourceImage.url)} alt="" className="w-full rounded-xl border border-white/10" />
            </div>
          )}
        </aside>
      </div>

      {/* Background manager dialog */}
      <BackgroundManager
        open={bgManagerOpen}
        onClose={() => setBgManagerOpen(false)}
        backgrounds={backgrounds}
        onChange={fetchBackgrounds}
      />
    </div>
  );
};

// ---------- Sub-components ----------

const Stepper = ({ step }) => {
  const labels = ["Fahrzeug", "Foto", "Hintergrund", "Anpassen", "Ergebnis"];
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1">
      {labels.map((label, idx) => {
        const num = idx + 1;
        const done = step > num;
        const active = step === num;
        return (
          <div key={label} className="flex items-center flex-shrink-0">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border ${active ? "border-[var(--na-red)] bg-[var(--na-red)]/10" : done ? "border-white/15 bg-white/5" : "border-white/5 bg-transparent"}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${active ? "bg-[var(--na-red)] text-white" : done ? "bg-white text-[#1A1A22]" : "bg-white/10 text-zinc-500"}`}>
                {done ? <CheckCircle2 className="w-3 h-3" /> : num}
              </span>
              <span className={`text-xs uppercase tracking-widest ${active ? "text-white" : done ? "text-zinc-300" : "text-zinc-500"}`}>{label}</span>
            </div>
            {num < labels.length && <ChevronRight className="w-3.5 h-3.5 text-zinc-600 mx-1" />}
          </div>
        );
      })}
    </div>
  );
};

const Section = ({ title, active, children }) => (
  <section className={`rounded-2xl border ${active ? "border-white/10 bg-[#2E2E3C]" : "border-white/5 bg-[#2E2E3C]/40 opacity-70"} p-5 lg:p-6`}>
    <h3 className="font-display text-sm font-semibold text-zinc-300 uppercase tracking-widest mb-4">{title}</h3>
    {children}
  </section>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
    <span className="text-sm text-white text-right truncate ml-2 min-w-0">{value}</span>
  </div>
);

// ---------- Background Manager ----------

const BackgroundManager = ({ open, onClose, backgrounds, onChange }) => {
  const [newBg, setNewBg] = React.useState({
    name: "",
    description: "",
    style_type: "custom",
    prompt: "",
    template_url: "",
    has_baked_logo: false,
    logo_overlay_enabled: false,
    logo_variant: "black_n_red_a",
    logo_backlit_panel: false,
  });
  const [uploadingBg, setUploadingBg] = React.useState(false);
  const [creating, setCreating] = React.useState(false);

  // Enforce no-duplicate-logo invariant in the form UI
  React.useEffect(() => {
    if (newBg.has_baked_logo && newBg.logo_overlay_enabled) {
      setNewBg((b) => ({ ...b, logo_overlay_enabled: false }));
    }
  }, [newBg.has_baked_logo, newBg.logo_overlay_enabled]);

  const uploadPreview = async (file) => {
    if (!file) return;
    setUploadingBg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNewBg((b) => ({ ...b, template_url: res.data.url }));
      toast.success("Vorschaubild hochgeladen");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploadingBg(false);
    }
  };

  const create = async () => {
    if (!newBg.name || !newBg.template_url) {
      toast.error("Name und Hintergrund-Bild sind erforderlich.");
      return;
    }
    if (newBg.has_baked_logo && newBg.logo_overlay_enabled) {
      toast.error("Doppeltes Logo nicht möglich — bitte nur eine Option aktivieren.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/backgrounds", { ...newBg, preview_url: newBg.template_url });
      toast.success("Hintergrund hinzugefügt");
      setNewBg({
        name: "", description: "", style_type: "custom", prompt: "",
        template_url: "", has_baked_logo: false, logo_overlay_enabled: false,
        logo_variant: "black_n_red_a", logo_backlit_panel: false,
      });
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (bg) => {
    await api.patch(`/admin/backgrounds/${bg.id}`, { active: !bg.active });
    onChange();
  };

  const setDefault = async (bg) => {
    await api.patch(`/admin/backgrounds/${bg.id}`, { is_default: true });
    toast.success(`Standard: ${bg.name}`);
    onChange();
  };

  const remove = async (bg) => {
    if (!window.confirm(`Hintergrund "${bg.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/backgrounds/${bg.id}`);
      toast.success("Hintergrund gelöscht");
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="background-manager-dialog"
        className="bg-[#1E1E28] border-white/10 text-white max-w-3xl max-h-[88vh] overflow-y-auto rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Studio-Hintergründe verwalten</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            Eigene Showroom-Vorlagen hochladen, bearbeiten oder löschen. Hintergründe mit „Logo bereits eingebrannt" überspringen die automatische NA-Logo-Overlay-Stufe.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {backgrounds.map((bg) => (
            <div key={bg.id} data-testid={`bg-row-${bg.key || bg.id}`} className="flex items-center gap-4 rounded-2xl border border-white/5 bg-[#2E2E3C] p-3">
              <div className="w-20 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-[#1A1A22] border border-white/10">
                {bg.preview_url && <img src={bg.preview_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display font-semibold text-white">{bg.name}</p>
                  {bg.is_default && <span className="text-[9px] uppercase tracking-widest text-yellow-300 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-2 py-0.5">Standard</span>}
                  {bg.is_custom && <span className="text-[9px] uppercase tracking-widest text-zinc-300 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">Custom</span>}
                  {!bg.active && <span className="text-[9px] uppercase tracking-widest text-zinc-400 bg-zinc-700/40 border border-white/10 rounded-full px-2 py-0.5">Inaktiv</span>}
                  {bg.has_baked_logo && <span className="text-[9px] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">Logo eingebrannt</span>}
                  {bg.logo_overlay_enabled && !bg.has_baked_logo && (
                    <span className="text-[9px] uppercase tracking-widest text-[var(--na-red)] bg-[var(--na-red)]/10 border border-[var(--na-red)]/30 rounded-full px-2 py-0.5">
                      Logo {bg.logo_variant === "white_n_red_a" ? "Weiß-N" : "Schwarz-N"}
                    </span>
                  )}
                  {bg.logo_backlit_panel && bg.logo_overlay_enabled && (
                    <span className="text-[9px] uppercase tracking-widest text-zinc-300 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">Wand-Plakette</span>
                  )}
                  {!bg.logo_overlay_enabled && !bg.has_baked_logo && (
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 bg-zinc-800/60 border border-white/10 rounded-full px-2 py-0.5">Ohne Logo</span>
                  )}
                </div>
                <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{bg.description}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => toggleActive(bg)} data-testid={`bg-toggle-${bg.id}`} className="text-xs uppercase tracking-widest text-zinc-300 hover:text-white px-2 py-1 border border-white/10 rounded-xl">
                  {bg.active ? "Deaktivieren" : "Aktivieren"}
                </button>
                {!bg.is_default && bg.active && (
                  <button onClick={() => setDefault(bg)} data-testid={`bg-default-${bg.id}`} className="text-xs uppercase tracking-widest text-zinc-300 hover:text-white px-2 py-1 border border-white/10 rounded-xl">
                    <Star className="w-3 h-3 inline mr-1" /> Standard
                  </button>
                )}
                {bg.is_custom && (
                  <button onClick={() => remove(bg)} data-testid={`bg-delete-${bg.id}`} className="p-1.5 rounded-xl hover:bg-white/10 text-[var(--na-red)]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* New custom background form */}
          <div className="rounded-2xl border border-dashed border-white/15 bg-[#2E2E3C]/40 p-5 space-y-4">
            <h4 className="font-display font-semibold text-white">Eigenen Hintergrund hochladen</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Name *</Label>
                <Input data-testid="bg-new-name" value={newBg.name} onChange={(e) => setNewBg({ ...newBg, name: e.target.value })} placeholder="z.B. NA Garage" className="bg-[#1A1A22] border-white/10 text-white rounded-xl" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Stil</Label>
                <Select value={newBg.style_type} onValueChange={(v) => setNewBg({ ...newBg, style_type: v })}>
                  <SelectTrigger data-testid="bg-new-style" className="bg-[#1A1A22] border-white/10 text-white rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2E2E3C] border-white/10 text-white">
                    {STYLE_TYPES.map((s) => (<SelectItem key={s.key} value={s.key} className="focus:bg-white/10 focus:text-white">{s.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Beschreibung</Label>
                <Input data-testid="bg-new-description" value={newBg.description} onChange={(e) => setNewBg({ ...newBg, description: e.target.value })} placeholder="Kurze Beschreibung dieses Hintergrunds" className="bg-[#1A1A22] border-white/10 text-white rounded-xl" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5 block">KI-Prompt (optional)</Label>
                <Input data-testid="bg-new-prompt" value={newBg.prompt} onChange={(e) => setNewBg({ ...newBg, prompt: e.target.value })} placeholder="Eigener Prompt für die Bildgenerierung" className="bg-[#1A1A22] border-white/10 text-white rounded-xl" />
              </div>
              <div className="sm:col-span-2 flex flex-col gap-2 rounded-xl border border-white/10 bg-[#1A1A22] p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="bg-new-has-baked"
                    checked={newBg.has_baked_logo}
                    onChange={(e) => setNewBg({ ...newBg, has_baked_logo: e.target.checked })}
                    className="mt-0.5 w-4 h-4 accent-[var(--na-red)]"
                  />
                  <span className="text-xs text-zinc-300 leading-snug">
                    <span className="font-medium text-white">NA Logo bereits im Bild</span>
                    <br />
                    <span className="text-zinc-400">Aktivieren, wenn das hochgeladene Bild bereits ein NA Logo enthält.</span>
                  </span>
                </label>
                <label className={`flex items-start gap-2 ${newBg.has_baked_logo ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}>
                  <input
                    type="checkbox"
                    data-testid="bg-new-overlay"
                    checked={newBg.logo_overlay_enabled}
                    disabled={newBg.has_baked_logo}
                    onChange={(e) => setNewBg({ ...newBg, logo_overlay_enabled: e.target.checked })}
                    className="mt-0.5 w-4 h-4 accent-[var(--na-red)]"
                  />
                  <span className="text-xs text-zinc-300 leading-snug">
                    <span className="font-medium text-white">NA Logo Overlay aktivieren</span>
                    <br />
                    <span className="text-zinc-400">Das echte NA SVG Logo wird deterministisch über dem Ergebnis platziert.</span>
                  </span>
                </label>
                {newBg.logo_overlay_enabled && !newBg.has_baked_logo && (
                  <div className="pl-6 mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1 block">Logo-Variante</Label>
                      <Select
                        value={newBg.logo_variant}
                        onValueChange={(v) => setNewBg({ ...newBg, logo_variant: v })}
                      >
                        <SelectTrigger data-testid="bg-new-logo-variant" className="bg-[#0F0F14] border-white/10 text-white rounded-xl h-9 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#2E2E3C] border-white/10 text-white">
                          <SelectItem value="black_n_red_a" className="text-xs focus:bg-white/10 focus:text-white">Schwarzes N + rotes A (Standard)</SelectItem>
                          <SelectItem value="white_n_red_a" className="text-xs focus:bg-white/10 focus:text-white">Weißes N + rotes A (Legacy)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer pt-5">
                      <input
                        type="checkbox"
                        data-testid="bg-new-backlit"
                        checked={newBg.logo_backlit_panel}
                        onChange={(e) => setNewBg({ ...newBg, logo_backlit_panel: e.target.checked })}
                        className="mt-0.5 w-4 h-4 accent-[var(--na-red)]"
                      />
                      <span className="text-xs text-zinc-300 leading-snug">
                        <span className="font-medium text-white">Wand-Plakette</span>
                        <br />
                        <span className="text-zinc-400">Subtile helle Fläche hinter dem Logo (für dunkle Wände mit schwarzem N).</span>
                      </span>
                    </label>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5 block">Hintergrund-Bild *</Label>
                {newBg.template_url ? (
                  <div className="flex items-center gap-3">
                    <img src={resolveImageUrl(newBg.template_url)} alt="" className="w-20 h-16 rounded-xl object-cover border border-white/10" />
                    <button onClick={() => setNewBg({ ...newBg, template_url: "" })} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1">
                      <X className="w-3 h-3" /> Entfernen
                    </button>
                  </div>
                ) : (
                  <label className="block">
                    <input type="file" accept="image/*,.heic,.heif" onChange={(e) => uploadPreview(e.target.files?.[0])} className="hidden" data-testid="bg-new-upload" />
                    <div className="rounded-xl border border-dashed border-white/15 bg-[#1A1A22] hover:bg-[#2E2E3C] p-4 text-center cursor-pointer">
                      {uploadingBg ? <Loader2 className="w-5 h-5 mx-auto animate-spin text-[var(--na-red)]" /> : <><Plus className="w-4 h-4 mx-auto text-zinc-400 mb-1" /><span className="text-xs text-zinc-400">Bild auswählen</span></>}
                    </div>
                  </label>
                )}
              </div>
              <Button onClick={create} disabled={creating || !newBg.name || !newBg.template_url} data-testid="bg-new-create" className="bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl">
                {creating ? "Speichert…" : "Hinzufügen"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoStudio;
